import {
  REVIEWABLE_DOCUMENT_CATEGORIES,
  ReviewableDocumentCategoryKey,
} from '@bordrio/shared';
import { useTranslation } from 'react-i18next';
import { useMatch } from 'react-router';
import { StepListStep } from '@src/components/StepsList';
import {
  ReviewableDocumentFragment,
  ReviewableDocumentReviewerType,
} from '@src/generated/graphql';
import { useToast } from '@src/hooks/useToast';

interface UseReuploadDocumentsFlowPendingResult {
  type: 'pending';
}

interface UseReuploadDocumentsFlowNavigationResult<T> {
  type: 'navigation';
  sidebarSteps: StepListStep[];
  previousUrl: string | null;
  getNextUrl: () => string;
  document: T;
}

interface UseReuploadDocumentsFlowRedirectResult {
  type: 'redirect';
  redirectTo: string;
}

type UseReuploadDocumentsFlowResult<T> =
  | UseReuploadDocumentsFlowPendingResult
  | UseReuploadDocumentsFlowNavigationResult<T>
  | UseReuploadDocumentsFlowRedirectResult;

interface UseReuploadDocumentsFlowOptions<T> {
  documents?: T[];
  extractReviewableDocument: (document: T) => ReviewableDocumentFragment;
  extractTitle: (document: T) => string;
  pathPrefix: string;
  reviewerType: ReviewableDocumentReviewerType;
  documentCategoryOrder?: ReviewableDocumentCategoryKey[];
}

export function useReuploadDocumentsFlow<T>({
  documents,
  extractReviewableDocument,
  extractTitle,
  pathPrefix,
  reviewerType,
  documentCategoryOrder,
}: UseReuploadDocumentsFlowOptions<T>): UseReuploadDocumentsFlowResult<T> {
  const routeMatch = useMatch(`${pathPrefix}/:documentId`);
  const toast = useToast();
  const { t } = useTranslation();

  const currentDocumentId = routeMatch?.params.documentId;

  function makeUrlToStep(documentId: string): string {
    return `${pathPrefix}/${documentId}`;
  }

  if (!documents) {
    return { type: 'pending' };
  }

  // make sure we have pending documents
  const pendingDocuments = documents.filter(
    (doc) =>
      !extractReviewableDocument(doc).currentVersion?.reviews.some(
        (r) => r.isApproved && r.reviewerType === reviewerType,
      ),
  );

  if (!pendingDocuments.length) {
    return { type: 'redirect', redirectTo: '/' };
  }

  const categoryOrder = documentCategoryOrder?.map(
    (o) => REVIEWABLE_DOCUMENT_CATEGORIES[o].id,
  );

  const orderedDocuments = categoryOrder
    ? pendingDocuments.sort((a, b) => {
        const aIdx = categoryOrder.indexOf(
          extractReviewableDocument(a).categoryId,
        );
        const bIdx = categoryOrder.indexOf(
          extractReviewableDocument(b).categoryId,
        );
        return aIdx - bIdx;
      })
    : pendingDocuments;

  const availableSteps = orderedDocuments.map((doc) => ({
    id: extractReviewableDocument(doc).id,
    hasNewUpload:
      extractReviewableDocument(doc).currentVersion?.reviews.length === 0,
    title: extractTitle(doc),
    doc,
  }));

  // find maximum step they can be at
  const maxAllowedStepIdx = (() => {
    const lastValidStep = availableSteps.findIndex(
      (step) => !step.hasNewUpload,
    );
    return lastValidStep === -1 ? availableSteps.length - 1 : lastValidStep;
  })();

  const currentStepIdx = availableSteps.findIndex(
    (step) => step.id === currentDocumentId,
  );

  // direct the user to the appropriate step for where they are if we don't have an explicit step
  // or they should not access the step
  if (
    !currentDocumentId ||
    currentStepIdx === -1 ||
    (maxAllowedStepIdx !== -1 && currentStepIdx > maxAllowedStepIdx)
  ) {
    return {
      type: 'redirect',
      redirectTo: makeUrlToStep(availableSteps[maxAllowedStepIdx].id),
    };
  }

  // get previous step
  const previousStep =
    currentStepIdx === 0 ? null : availableSteps[currentStepIdx - 1];

  // generate sidebar steps
  const sidebarSteps: StepListStep[] = availableSteps.map((step, idx) => {
    let status: StepListStep['status'] = 'complete';
    if (idx === currentStepIdx) {
      status = 'current';
    } else if (idx > currentStepIdx) {
      status = 'upcoming';
    }
    return {
      name: step.title,
      href: idx <= maxAllowedStepIdx ? makeUrlToStep(step.id) : '#',
      status,
    };
  });

  return {
    type: 'navigation',
    sidebarSteps,
    previousUrl: previousStep && makeUrlToStep(previousStep.id),
    document: availableSteps[currentStepIdx].doc,
    getNextUrl: () => {
      const nextStepIdx = currentStepIdx + 1;
      if (nextStepIdx >= availableSteps.length) {
        toast.success(
          t(
            'translation:reupload.success_toast',
            'Your documents have been successfully updated!',
          ),
        );
        return '/';
      }
      return makeUrlToStep(availableSteps[nextStepIdx].id);
    },
  };
}
